import { Project } from '@prisma/client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@zond/ui';
import { cn } from '@zond/utils';
import { FolderIcon, Users2Icon, XIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetProjects } from '../../projects/projects.queries';

interface User {
  id: string;
  email: string;
  firstname?: string;
  lastname?: string;
  displayname?: string;
  avatar?: string | null;
  avatarBackground?: string;
  avatarEmoji?: string;
}

interface ExtendedProject extends Project {
  UserProject: Array<{
    user: User;
  }>;
}

interface ProjectUserSelectorProps {
  currentProjectId?: string;
  currentUserId?: string;
  scrollToSelected?: boolean;
}

const formatUserCount = (count: number) => {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  if (count >= 500) {
    return '500+';
  }
  if (count >= 200) {
    return '200+';
  }
  if (count >= 100) {
    return '100+';
  }
  return count.toString();
};

export const ProjectUserSelector: React.FC<ProjectUserSelectorProps> = ({
  currentProjectId,
  currentUserId,
  scrollToSelected = true,
}) => {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { data: projects, isLoading: isProjectsLoading } = useGetProjects();
  const [projectSearch, setProjectSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [projectSelectOpen, setProjectSelectOpen] = useState(false);
  const [userSelectOpen, setUserSelectOpen] = useState(false);

  // Get current project
  const currentProject = useMemo(
    () => projects?.find((p) => p.id.toString() === currentProjectId) as ExtendedProject | undefined,
    [projects, currentProjectId]
  );

  // Get current user
  const currentUserData = useMemo(
    () => currentProject?.UserProject.find((up) => up.user.id.toString() === currentUserId)?.user,
    [currentProject, currentUserId]
  );

  // Filter projects by search query
  const filteredProjects = useMemo(
    () => projects?.filter((project) => project.name.toLowerCase().includes(projectSearch.toLowerCase())),
    [projects, projectSearch]
  );

  // Filter users by search query
  const filteredUsers = useMemo(
    () =>
      currentProject?.UserProject.filter((up) => {
        const searchLower = userSearch.toLowerCase();
        const user = up.user;
        return (
          user.email.toLowerCase().includes(searchLower) ||
          user.displayname?.toLowerCase().includes(searchLower) ||
          `${user.firstname} ${user.lastname}`.toLowerCase().includes(searchLower)
        );
      }),
    [currentProject, userSearch]
  );

  // Effect for scrolling to selected item
  useEffect(() => {
    if (projectSelectOpen && scrollToSelected && currentProjectId) {
      const selectedItem = document.querySelector(`[data-project-id="${currentProjectId}"]`);
      selectedItem?.scrollIntoView({ block: 'center' });
    }
    if (userSelectOpen && scrollToSelected && currentUserId) {
      const selectedItem = document.querySelector(`[data-user-id="${currentUserId}"]`);
      selectedItem?.scrollIntoView({ block: 'center' });
    }
  }, [projectSelectOpen, userSelectOpen, currentProjectId, currentUserId, scrollToSelected]);

  // Handle project change
  const handleProjectChange = (projectId: string) => {
    const selectedProject = projects?.find((p) => p.id.toString() === projectId) as
      | ExtendedProject
      | undefined;
    if (!selectedProject) return;

    // Get users from the selected project
    const projectUsers = selectedProject.UserProject.map((up) => up.user);

    // Keep the 'All' choice if it was previously selected
    if (currentUserId === 'All') {
      setProjectSelectOpen(false);
      navigate(`/${workspaceId}/reports/${projectId}/user/All${window.location.search}`);
      return;
    }

    // If current user exists in new project, keep them selected
    const userExists = projectUsers.some((user) => user.id.toString() === currentUserId);
    const newUserId = userExists ? currentUserId : projectUsers[0]?.id.toString();

    if (newUserId) {
      setProjectSelectOpen(false);
      navigate(`/${workspaceId}/reports/${projectId}/user/${newUserId}${window.location.search}`);
    }
  };

  // Handle user change
  const handleUserChange = (userId: string) => {
    if (currentProjectId) {
      setUserSelectOpen(false);
      navigate(`/${workspaceId}/reports/${currentProjectId}/user/${userId}${window.location.search}`);
    }
  };

  // Function to get user initials
  const getUserInitials = (user: User) => {
    if (user.displayname) {
      return user.displayname
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase();
    }
    if (user.firstname || user.lastname) {
      return `${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}`.toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="flex gap-4">
      <Select
        open={projectSelectOpen}
        onOpenChange={setProjectSelectOpen}
        value={currentProjectId}
        onValueChange={handleProjectChange}
        disabled={isProjectsLoading}
      >
        <SelectTrigger className="w-[240px] [&>span:last-child]:mr-2 hover:bg-accent hover:text-accent-foreground transition-colors outline-none">
          <SelectValue>
            {currentProject && (
              <div className="flex items-center w-full">
                {currentProject.icon ? (
                  (() => {
                    // @ts-ignore - we know these icons exist
                    const IconComponent = LucideIcons[currentProject.icon];
                    return <IconComponent className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />;
                  })()
                ) : (
                  <FolderIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span
                  className="flex-1 min-w-[135px] whitespace-nowrap mr-2 flex items-center"
                  title={currentProject.name}
                >
                  <span className="inline-block text-ellipsis overflow-hidden">{currentProject.name}</span>
                </span>
                <div className="flex-shrink-0 flex min-h-5 min-w-5 px-1.5 items-center justify-center rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {formatUserCount(currentProject.UserProject.length)}
                </div>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="w-[240px]">
          <Command shouldFilter={false} className="overflow-hidden w-full">
            <div className="px-2 py-2 relative">
              <CommandInput
                placeholder={t('selector.searchProjects')}
                value={projectSearch}
                onValueChange={setProjectSearch}
                className="h-9 [&_input]:border-0 [&_input]:focus-visible:ring-0 [&_input]:pr-8"
              />
              {projectSearch && (
                <button
                  onClick={() => setProjectSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-sm opacity-50 hover:opacity-100 transition-opacity"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
              {filteredProjects?.length === 0 ? (
                <CommandEmpty className="py-6 text-center text-sm">
                  {t('selector.noProjectsFound')}
                </CommandEmpty>
              ) : (
                filteredProjects?.map((project) => {
                  const userCount = project.UserProject.length;
                  const isDisabled = userCount === 0;
                  const isSelected = project.id.toString() === currentProjectId;
                  const projectId = project.id.toString();
                  const iconName = project.icon;
                  // @ts-ignore - we know these icons exist
                  const Icon = iconName ? LucideIcons[iconName] : FolderIcon;

                  return (
                    <CommandItem
                      key={project.id}
                      value={projectId}
                      onSelect={handleProjectChange}
                      disabled={isDisabled}
                      data-project-id={project.id}
                      className={cn(
                        'px-2 py-1.5 relative transition-colors duration-200 group',
                        isDisabled && 'opacity-50 cursor-not-allowed',
                        isSelected
                          ? 'bg-accent data-[state=open]:bg-accent'
                          : 'hover:bg-accent/20 data-[state=open]:bg-transparent'
                      )}
                    >
                      <div className="flex items-center w-full">
                        {iconName ? (
                          (() => {
                            // @ts-ignore - we know these icons exist
                            const IconComponent = LucideIcons[iconName];
                            return <IconComponent className="h-4 w-4 mr-2 text-muted-foreground" />;
                          })()
                        ) : (
                          <FolderIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                        )}
                        <span
                          className="flex-1 min-w-0 whitespace-nowrap mr-2 flex items-center"
                          title={project.name}
                        >
                          <span className="inline-block text-ellipsis overflow-hidden">{project.name}</span>
                        </span>
                        <div
                          className={cn(
                            'flex-shrink-0 flex min-h-5 min-w-5 px-1.5 items-center justify-center rounded-full text-xs font-medium transition-colors duration-200',
                            isDisabled ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
                          )}
                        >
                          {formatUserCount(userCount)}
                        </div>
                      </div>
                    </CommandItem>
                  );
                })
              )}
            </div>
          </Command>
        </SelectContent>
      </Select>

      <Select
        open={userSelectOpen}
        onOpenChange={setUserSelectOpen}
        value={currentUserId}
        onValueChange={handleUserChange}
        disabled={!currentProject || currentProject.UserProject.length === 0}
      >
        <SelectTrigger className="w-[240px] [&>span:last-child]:mr-2 hover:bg-accent hover:text-accent-foreground transition-colors outline-none">
          <SelectValue>
            {currentProject &&
              (currentUserId === 'All' ? (
                <div className="flex items-center w-full">
                  <span
                    className="flex-1 min-w-[170px] whitespace-nowrap flex items-center"
                    title={t('selector.allUsers')}
                  >
                    <span className="inline-block text-ellipsis overflow-hidden">
                      {t('selector.allUsers')}
                    </span>
                  </span>
                </div>
              ) : (
                currentUserData && (
                  <div className="flex items-center w-full">
                    <Avatar className="h-5 w-5 mr-2">
                      <AvatarImage
                        src={(currentUserData.avatar && currentUserData.avatar.trim()) || undefined}
                        alt={currentUserData.displayname || currentUserData.email}
                      />
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {getUserInitials(currentUserData)}
                      </AvatarFallback>
                    </Avatar>
                    <span
                      className="flex-1 min-w-[170px] whitespace-nowrap mr-2 flex items-center"
                      title={currentUserData.email}
                    >
                      <span className="inline-block text-ellipsis overflow-hidden">
                        {currentUserData.displayname ?? currentUserData.email}
                      </span>
                    </span>
                  </div>
                )
              ))}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="w-[240px]">
          <Command shouldFilter={false} className="overflow-hidden w-full">
            <div className="px-2 py-2 relative">
              <CommandInput
                placeholder={t('selector.searchUsers')}
                value={userSearch}
                onValueChange={setUserSearch}
                className="h-9 [&_input]:border-0 [&_input]:focus-visible:ring-0 [&_input]:pr-8"
              />
              {userSearch && (
                <button
                  onClick={() => setUserSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-sm opacity-50 hover:opacity-100 transition-opacity"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
              {/* Project Report option (All) */}
              {currentProject && !userSearch && (
                <CommandItem
                  value="All"
                  onSelect={handleUserChange}
                  data-user-id="All"
                  className={cn(
                    'px-2 py-1.5 relative transition-colors duration-200 group',
                    currentUserId === 'All'
                      ? 'bg-accent data-[state=open]:bg-accent'
                      : 'hover:bg-accent/20 data-[state=open]:bg-transparent'
                  )}
                >
                  <div className="flex items-center w-full">
                    <span className="flex-1 min-w-0 whitespace-nowrap flex items-center ml-1">
                      <span className="inline-block text-ellipsis overflow-hidden">
                        {t('selector.allUsers')}
                      </span>
                    </span>
                  </div>
                </CommandItem>
              )}

              {filteredUsers?.length === 0 ? (
                <CommandEmpty className="py-6 text-center text-sm">{t('selector.noUsersFound')}</CommandEmpty>
              ) : (
                filteredUsers?.map((up) => {
                  const isSelected = up.user.id.toString() === currentUserId;
                  const userId = up.user.id.toString();
                  const user = up.user;

                  return (
                    <CommandItem
                      key={up.user.id}
                      value={userId}
                      onSelect={handleUserChange}
                      data-user-id={userId}
                      className={cn(
                        'px-2 py-1.5 relative transition-colors duration-200 group',
                        isSelected
                          ? 'bg-accent data-[state=open]:bg-accent'
                          : 'hover:bg-accent/20 data-[state=open]:bg-transparent'
                      )}
                    >
                      <div className="flex items-center w-full">
                        <Avatar className="h-5 w-5 mr-2">
                          <AvatarImage
                            src={(user.avatar && user.avatar.trim()) || undefined}
                            alt={user.displayname || user.email}
                          />
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {getUserInitials(user)}
                          </AvatarFallback>
                        </Avatar>
                        <span
                          className="flex-1 min-w-0 whitespace-nowrap mr-2 flex items-center"
                          title={up.user.email}
                        >
                          <span className="inline-block text-ellipsis overflow-hidden">
                            {up.user.displayname ?? up.user.email}
                          </span>
                        </span>
                      </div>
                    </CommandItem>
                  );
                })
              )}
            </div>
          </Command>
        </SelectContent>
      </Select>
    </div>
  );
};
